// src/screens/ProfileSettingsScreen.js
// ============================================================================
// PROFESSIONAL PROFILE SETTINGS SCREEN
// Modern, clean design for competitive gaming platform
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Alert,
  ActivityIndicator, ScrollView, Image, Platform, SafeAreaView, Linking, Share
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { signOut, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebaseConfig';
import { signOutWithDesktopPush, isPhoneOnlyUser, PHONE_NO_ACCOUNT_TEXT } from '../utils/phoneNumber';
import { useError } from '../components/ErrorContext';
import * as ImagePicker from 'expo-image-picker';
import Header from '../components/Header';
import useCritsEnabled from '../hooks/useCritsEnabled';
import useMembershipsLive from '../hooks/useMembershipsLive';

// Icon imports - using @expo/vector-icons (built into Expo)
import { MaterialCommunityIcons, Ionicons, FontAwesome, Feather } from '@expo/vector-icons';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

// Icon helper - maps icon names to @expo/vector-icons components
const getIcon = (iconName, size = 20, color = '#FCD000') => {
  const iconMap = {
    'stats': <Ionicons name="stats-chart" size={size} color={color} />,
    'flash': <Ionicons name="flash" size={size} color={color} />,
    'videocam': <Ionicons name="videocam" size={size} color={color} />,
    'checkmark': <Ionicons name="checkmark-circle" size={size} color={color} />,
    'wallet': <Ionicons name="wallet" size={size} color={color} />,
    'game-controller': <Ionicons name="game-controller" size={size} color={color} />,
    'star': <FontAwesome name="star" size={size} color={color} />,
    'trophy': <Ionicons name="trophy" size={size} color={color} />,
    'person': <Ionicons name="person" size={size} color={color} />,
    'settings': <Ionicons name="settings" size={size} color={color} />,
    'lock': <Ionicons name="lock-closed" size={size} color={color} />,
    'refresh': <Ionicons name="refresh" size={size} color={color} />,
    'help': <Ionicons name="help-circle" size={size} color={color} />,
    'link': <Ionicons name="link" size={size} color={color} />,
    'cube': <Ionicons name="cube" size={size} color={color} />,
    'film': <Ionicons name="film" size={size} color={color} />,
    'ribbon': <Ionicons name="ribbon" size={size} color={color} />,
  };
  return iconMap[iconName] || <Ionicons name="ellipse" size={size} color={color} />;
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionIcon}>{getIcon(icon, 20, '#FCD000')}</View>
    <View style={styles.sectionHeaderText}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {subtitle && <Text style={styles.sectionSubtitle}>{subtitle}</Text>}
    </View>
  </View>
);

const StatCard = ({ value, label, accent, onPress }) => (
  <View style={styles.statCardWrapper}>
    <TouchableOpacity 
      style={[styles.statCard, accent && styles.statCardAccent]} 
      onPress={onPress}
      disabled={!onPress}
      activeOpacity={onPress ? 0.7 : 1}
    >
      <Text style={[styles.statValue, accent && styles.statValueAccent]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
      {onPress && <Ionicons name="arrow-forward" size={16} color="#FCD000" style={styles.statArrow} />}
    </TouchableOpacity>
  </View>
);

const MenuCard = ({ icon, title, subtitle, onPress, badge, variant }) => (
  <TouchableOpacity 
    style={[
      styles.menuCard,
      variant === 'primary' && styles.menuCardPrimary,
      variant === 'featured' && styles.menuCardFeatured,
      variant === 'danger' && styles.menuCardDanger,
    ]} 
    onPress={onPress}
    activeOpacity={0.7}
  >
    <View style={styles.menuIcon}>{getIcon(icon, 22, variant === 'danger' ? '#E74C3C' : '#FCD000')}</View>
    <View style={styles.menuContent}>
      <Text style={[
        styles.menuTitle,
        variant === 'primary' && styles.menuTitlePrimary,
        variant === 'danger' && styles.menuTitleDanger,
      ]}>{title}</Text>
      {subtitle && <Text style={styles.menuSubtitle}>{subtitle}</Text>}
    </View>
    {badge && (
      <View style={[styles.menuBadge, badge.type === 'new' && styles.menuBadgeNew]}>
        <Text style={styles.menuBadgeText}>{badge.text}</Text>
      </View>
    )}
    <Feather name="chevron-right" size={20} color={variant === 'danger' ? '#E74C3C' : '#666'} style={styles.menuArrow} />
  </TouchableOpacity>
);

const InfoRow = ({ label, value, isLast, actionLabel, onAction }) => (
  <View style={[styles.infoRow, isLast && styles.infoRowLast]}>
    <Text style={styles.infoLabel}>{label}</Text>
    <View style={styles.infoValueWrap}>
      <Text style={styles.infoValue} numberOfLines={1}>{value || '-'}</Text>
      {actionLabel && onAction ? (
        <TouchableOpacity onPress={onAction}>
          <Text style={styles.infoAction}>{actionLabel}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  </View>
);

// ============================================================================
// REFER A FRIEND — two-sided Crit referral (comp signal #5)
// Fetches the user's share link + reward + stats from getMyReferral. Hidden if
// referrals are disabled (settings/referrals) or the call fails.
// ============================================================================

const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled — Crit copy only

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const fns = getFunctions(undefined, 'us-central1');
        const res = await httpsCallable(fns, 'getMyReferral')();
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const shareUrl = data && data.shareUrl;
  const reward = (data && data.refereeRewardCrits) || 0;
  const conversions = (data && data.stats && data.stats.conversions) || 0;
  const pending = (data && data.stats && data.stats.pending) || 0;
  const critsEarned = (data && data.stats && data.stats.critsEarned) || 0;
  const message = critsEnabled
    ? `Join me on CritCash — compete, climb the ladder, and earn. Sign up with my link; once you verify your account and play an event, we each get ${reward} Crits: ${shareUrl}`
    : `Join me on CritCash, the streaming platform with the tools built in. Sign up with my link: ${shareUrl}`;

  const onCopy = async () => {
    if (!shareUrl) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        await Share.share({ message: shareUrl });
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* user cancelled / unsupported */ }
  };

  const onShare = async () => {
    if (!shareUrl) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: 'CritCash', text: message, url: shareUrl });
      } else if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        await Share.share({ message });
      }
    } catch (_) { /* user cancelled / unsupported */ }
  };

  // Hide entirely while loading, on error, or when referrals are turned off.
  if (loading) return null;
  if (!data || !data.enabled || !shareUrl) return null;

  return (
    <View style={styles.section}>
      <SectionHeader
        icon="link"
        title="Refer a Friend"
        subtitle={critsEnabled
          ? `You each get ${reward} Crits once they verify and compete in an event`
          : 'Invite friends to join you on CritCash'}
      />

      <View style={refStyles.card}>
        <Text style={refStyles.label}>YOUR INVITE LINK</Text>
        <View style={refStyles.linkRow}>
          <Text style={refStyles.link} numberOfLines={1}>{shareUrl}</Text>
          <TouchableOpacity style={refStyles.copyBtn} onPress={onCopy} activeOpacity={0.7}>
            <Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={16} color="#000" />
            <Text style={refStyles.copyBtnText}>{copied ? 'Copied' : 'Copy'}</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={refStyles.shareBtn} onPress={onShare} activeOpacity={0.8}>
          <Ionicons name="share-social" size={18} color="#FCD000" />
          <Text style={refStyles.shareBtnText}>Share your link</Text>
        </TouchableOpacity>

        <View style={refStyles.statsRow}>
          <View style={refStyles.stat}>
            <Text style={refStyles.statValue}>{conversions}</Text>
            <Text style={refStyles.statLabel}>Completed</Text>
          </View>
          {critsEnabled && (
            <>
              <View style={refStyles.statDivider} />
              <View style={refStyles.stat}>
                <Text style={refStyles.statValue}>{critsEarned}</Text>
                <Text style={refStyles.statLabel}>Crits earned</Text>
              </View>
            </>
          )}
        </View>

        {pending > 0 && (
          <Text style={refStyles.pendingNote}>
            {critsEnabled
              ? `${pending} invited — they earn you Crits once they verify and play an event.`
              : `${pending} invited; they show as completed once they verify and play an event.`}
          </Text>
        )}
      </View>
    </View>
  );
};

const refStyles = StyleSheet.create({
  card: { backgroundColor: '#141414', borderRadius: 12, borderWidth: 1, borderColor: '#222', padding: 16 },
  label: { color: '#888', fontSize: 11, fontWeight: '800', letterSpacing: 1, marginBottom: 8 },
  linkRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0c0c0c', borderRadius: 10, borderWidth: 1, borderColor: '#222', paddingLeft: 12, paddingRight: 6, paddingVertical: 6 },
  link: { flex: 1, color: '#fff', fontSize: 13, marginRight: 8 },
  copyBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FCD000', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  copyBtnText: { color: '#000', fontWeight: '800', fontSize: 13, marginLeft: 6 },
  shareBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 12, backgroundColor: '#1a1a1a', borderRadius: 10, borderWidth: 1, borderColor: '#333', paddingVertical: 12 },
  shareBtnText: { color: '#FCD000', fontWeight: '800', fontSize: 14, marginLeft: 8 },
  statsRow: { flexDirection: 'row', alignItems: 'center', marginTop: 16 },
  stat: { flex: 1, alignItems: 'center' },
  statDivider: { width: 1, height: 32, backgroundColor: '#222' },
  statValue: { color: '#FCD000', fontSize: 22, fontWeight: '900' },
  statLabel: { color: '#888', fontSize: 12, marginTop: 2 },
  pendingNote: { color: '#888', fontSize: 12, marginTop: 12, textAlign: 'center', lineHeight: 16 },
});

// ============================================================================
// PROFILE HEADER COMPONENT
// ============================================================================

const ProfileHeader = ({ userData, isUploading, onPhotoUpload, onEditProfile, onViewPublicProfile }) => {
  const verificationStatus = getVerificationStatus(userData);
  
  return (
    <View style={styles.profileHeader}>
      {/* Background Accent */}
      <View style={styles.profileHeaderBg} />
      
      {/* Profile Image */}
      <TouchableOpacity
        style={styles.avatarContainer}
        onPress={onPhotoUpload}
        disabled={isUploading}
        activeOpacity={0.8}
      >
        {isUploading ? (
          <View style={styles.avatarLoading}>
            <ActivityIndicator size="large" color="#FCD000" />
          </View>
        ) : (
          <>
            <Image
              source={{
                uri: userData?.profilePhotoUrl ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(userData?.userName || 'User')}&background=222&color=FCD000&size=200`
              }}
              style={styles.avatar}
            />
            <View style={styles.avatarEditBadge}>
              <Feather name="edit-2" size={14} color="#000" />
            </View>
          </>
        )}
      </TouchableOpacity>

      {/* Username & Status */}
      <Text style={styles.username}>{userData?.userName || 'Player'}</Text>
      
      <View style={styles.statusRow}>
        <View style={[
          styles.statusBadge,
          verificationStatus.isVerified ? styles.statusVerified : styles.statusPending
        ]}>
          <View style={[
            styles.statusIcon,
            !verificationStatus.isVerified && styles.statusIconPending
          ]}>
            {verificationStatus.isVerified 
              ? <Ionicons name="checkmark" size={14} color="#2ECC71" /> 
              : <Ionicons name="ellipse-outline" size={14} color="#F39C12" />
            }
          </View>
          <Text style={[
            styles.statusText,
            !verificationStatus.isVerified && styles.statusTextPending
          ]}>
            {verificationStatus.isVerified ? 'Verified' : 'Unverified'}
          </Text>
        </View>
        
        {userData?.isHost && (
          <View style={styles.hostBadge}>
            <FontAwesome name="star" size={12} color="#FCD000" style={{marginRight: 4}} />
            <Text style={styles.hostBadgeText}>HOST</Text>
          </View>
        )}
      </View>

      {/* Member Since */}
      <Text style={styles.memberSince}>
        Member since {formatMemberDate(userData?.createdAt)}
      </Text>

      {/* View public profile */}
      {onViewPublicProfile && (
        <TouchableOpacity
          style={{
            flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
            marginTop: 14, alignSelf: 'center', backgroundColor: '#1a1a1a',
            borderWidth: 1, borderColor: '#333', borderRadius: 10,
            paddingVertical: 10, paddingHorizontal: 16,
          }}
          onPress={onViewPublicProfile}
          activeOpacity={0.8}
        >
          <Ionicons name="person-circle-outline" size={18} color="#FCD000" style={{ marginRight: 8 }} />
          <Text style={{ color: '#FCD000', fontWeight: '800', fontSize: 14, marginRight: 6 }}>View public profile</Text>
          <Ionicons name="arrow-forward" size={16} color="#FCD000" />
        </TouchableOpacity>
      )}
    </View>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getVerificationStatus(userData) {
  const v = userData?.verification || {};
  
  // Check each verification type using correct field paths
  const isIdVerified = v.governmentId?.status === 'verified';
  const isPhoneVerified = userData?.phoneVerified === true; // phoneVerified is at root level
  const isTosAccepted = v.tos?.status === 'verified';
  
  return {
    isVerified: isIdVerified && isPhoneVerified && isTosAccepted,
    identity: isIdVerified,
    phone: isPhoneVerified,
    tos: isTosAccepted,
  };
}

function formatMemberDate(timestamp) {
  if (!timestamp) return 'Recently';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatBalance(balanceCents) {
  // Balance is stored in cents, divide by 100 to get dollars
  const dollars = (balanceCents || 0) / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatNumber(num) {
  return (num || 0).toLocaleString('en-US');
}

function formatWinRate(wins, losses) {
  const total = (wins || 0) + (losses || 0);
  if (total === 0) return '-';
  return `${Math.round((wins / total) * 100)}%`;
}

// ============================================================================
// MAIN SCREEN COMPONENT
// ============================================================================

export default function ProfileSettingsScreen({ navigation }) {
  const { showError } = useError();
  const [initializing, setInitializing] = useState(true);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled — the Crits stat card only
  // Channel memberships (evergreen 3.5a): the "Your memberships" entry and the
  // Creator Metrics source list appear once memberships have ever launched
  // (firstLiveAt persists, so a paused launch keeps them). Team memberships
  // (evergreen 3.5b): the "Your memberships" entry and the Creator Metrics
  // source list key on EITHER kind having ever launched (anyEverLaunched, as
  // the Wallet does per design 13.12), and the entry's subtitle names teams
  // once team memberships have (teamEverLaunched).
  const { teamEverLaunched, anyEverLaunched } = useMembershipsLive();
  const [isLoading, setLoading] = useState(false);
  // True once the users/public_profiles read for the CURRENT auth user has
  // settled — keeps the incomplete-profile card from flashing for a frame
  // between auth resolving and the doc read starting.
  const [profileLoaded, setProfileLoaded] = useState(false);
  // Set when the doc read itself threw (offline, rules, transient) — that is
  // NOT "onboarding incomplete", so it renders a retry, never the finish card.
  const [profileLoadError, setProfileLoadError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const isFocused = useIsFocused();

  const isGoogleUser = user?.providerData?.some(
    (provider) => provider.providerId === 'google.com'
  );

  // Auth state listener
  useEffect(() => {
    const subscriber = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (initializing) setInitializing(false);
    });
    return subscriber;
  }, [initializing]);

  // Load user data
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setUserData(null);
        setProfileLoaded(false);
        setProfileLoadError(null);
        return;
      }

      setLoading(true);
      setProfileLoadError(null);
      try {
        const [privateSnap, publicSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'public_profiles', user.uid)),
        ]);

        // A missing public_profile is a degraded-but-real account (legacy /
        // mid-repair) — render from the private doc rather than pretending
        // the member is logged out. A missing PRIVATE doc means onboarding
        // never finished; the render below sends them to complete it.
        if (privateSnap.exists()) {
          setUserData({ ...privateSnap.data(), ...(publicSnap.exists() ? publicSnap.data() : {}) });
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        setProfileLoadError(error?.message || 'Could not load your profile.');
      } finally {
        setLoading(false);
        setProfileLoaded(true);
      }
    };

    if (!initializing && isFocused) loadData();
  }, [user, initializing, isFocused, reloadTick]);

  // Photo upload handler
  const handlePhotoUpload = async () => {
    if (!user) return;

    const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permissionResult.granted) {
      showError('Permission Required', 'Please allow access to your photos.');
      return;
    }

    const pickerResult = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.2,
      base64: true,
    });

    if (pickerResult.canceled) return;

    setIsUploading(true);
    try {
      // Photos upload through a Cloud Function that writes to an opaque path
      // (avatars/<randomId>) and returns a critcash.com/avatar/<id> URL — the
      // URL never exposes the account UID or the Storage bucket. The function
      // also updates users/{uid} and public_profiles/{uid}.
      const asset = pickerResult.assets[0];
      let imageBase64 = asset.base64;
      if (!imageBase64) {
        // Fallback: derive base64 from the uri (e.g. platforms that skip base64).
        const blob = await (await fetch(asset.uri)).blob();
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }

      const uploadFn = httpsCallable(getFunctions(), 'uploadProfilePhoto');
      const { data } = await uploadFn({ imageBase64, contentType: 'image/jpeg' });
      const downloadURL = data?.url;
      if (!downloadURL) throw new Error('No URL returned');

      // Keep the Firebase Auth profile photo in sync (Firestore docs are
      // written server-side by the function).
      await updateProfile(user, { photoURL: downloadURL });

      setUserData(prev => ({ ...prev, profilePhotoUrl: downloadURL }));
      showError('Success', 'Profile photo updated!');
    } catch (error) {
      console.error('Photo upload error:', error);
      showError('Error', 'Failed to upload photo.');
    } finally {
      setIsUploading(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    const confirmLogout = Platform.OS === 'web'
      ? window.confirm('Are you sure you want to log out?')
      : true;
    
    if (Platform.OS !== 'web') {
      Alert.alert('Log Out', 'Are you sure you want to log out?', [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Log Out', 
          style: 'destructive',
          onPress: () => performLogout()
        },
      ]);
    } else if (confirmLogout) {
      performLogout();
    }
  };

  const performLogout = () => {
    signOutWithDesktopPush({ signOut, auth, isDesktop: typeof window !== 'undefined' && window.critcashDesktop?.isDesktopApp === true }) // desktop app: tell it first (S2.2 item 4)
      .then(() => navigation.reset({ index: 0, routes: [{ name: 'Landing' }] }))
      .catch((error) => showError('Error', 'Failed to log out.'));
  };

  // Loading state
  if (initializing || isLoading || (user && !profileLoaded)) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
        <Text style={styles.loadingText}>Loading profile...</Text>
      </View>
    );
  }

  // Not logged in — genuinely no auth session.
  if (!user) {
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="lock-closed" size={48} color="#666" style={styles.errorIcon} />
        <Text style={styles.errorTitle}>Not Logged In</Text>
        <Text style={styles.errorText}>Please log in to view your profile.</Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.replace('Login')}
        >
          <Text style={styles.primaryButtonText}>Go to Login</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // The read failed (offline / transient) — say so and offer a retry. Not the
  // same thing as an incomplete account, and never "Not Logged In".
  if (profileLoadError) {
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="cloud-offline-outline" size={48} color="#666" style={styles.errorIcon} />
        <Text style={styles.errorTitle}>Couldn't Load Your Profile</Text>
        <Text style={styles.errorText}>{profileLoadError}</Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => { setProfileLoaded(false); setProfileLoadError(null); setReloadTick((t) => t + 1); }}
        >
          <Text style={styles.primaryButtonText}>Try Again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Signed in, but onboarding never finished: no users doc (a Google signup
  // interrupted before the account doc was written) or a pending OAuth doc
  // (onboardingComplete:false). This used to fall through to "Not Logged In"
  // above and tell a real, signed-in member to log in again (2026-08-17).
  // Send them to the wizard's completion step instead.
  if (!userData || userData.onboardingComplete === false) {
    // Phone sign-in never creates an account (DESKTOP_APP_DESIGN_2026-10-20.md
    // S2.4, P3): a phone-only record (a ghost whose client delete failed) sees
    // the pinned message and Log out, never "Complete My Profile", the same
    // as FinishOnboardingModal's phone branch.
    if (isPhoneOnlyUser(user)) {
      return (
        <View style={styles.loadingContainer}>
          <Ionicons name="call-outline" size={48} color="#FCD000" style={styles.errorIcon} />
          <Text style={styles.errorText}>{PHONE_NO_ACCOUNT_TEXT}</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={performLogout}>
            <Text style={styles.primaryButtonText}>Log out</Text>
          </TouchableOpacity>
        </View>
      );
    }
    const providerId = user.providerData?.[0]?.providerId || '';
    const providerLabel = providerId === 'google.com' ? 'Google'
      : userData?.authProvider ? userData.authProvider.charAt(0).toUpperCase() + userData.authProvider.slice(1)
      : null;
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="person-add" size={48} color="#FCD000" style={styles.errorIcon} />
        <Text style={styles.errorTitle}>Finish Setting Up Your Account</Text>
        <Text style={styles.errorText}>
          You're signed in{user.email ? ` as ${user.email}` : ''}, but your profile isn't complete yet.
          It only takes a minute — pick a username and confirm a few details.
        </Text>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.replace('SignUp', {
            oauthCompletion: true,
            provider: providerLabel,
            email: user.email,
            displayName: user.displayName || '',
          })}
        >
          <Text style={styles.primaryButtonText}>Complete My Profile</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const addressStr = [
    userData.address?.street,
    userData.address?.city,
    userData.address?.state,
    userData.address?.zip
  ].filter(Boolean).join(', ');

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} /> 
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Profile Header */}
        <ProfileHeader
          userData={userData}
          isUploading={isUploading}
          onPhotoUpload={handlePhotoUpload}
          onViewPublicProfile={() => userData?.userName && navigation.navigate('PublicProfile', { userName: userData.userName })}
        />

        {/* Admin Button (if admin) */}
        {userData?.role === 'admin' && (
          <TouchableOpacity
            style={styles.adminBanner}
            onPress={() => navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="flash" size={20} color="#FCD000" />
            <Text style={styles.adminBannerText}>Admin Dashboard</Text>
            <Ionicons name="arrow-forward" size={16} color="#FCD000" />
          </TouchableOpacity>
        )}

        {/* Stats Section */}
        <View style={styles.section}>
          <SectionHeader icon="stats" title="Your Stats" />
          <View style={styles.statsGrid}>
            {critsEnabled && (
              <StatCard
                value={formatNumber(userData.critBalance)}
                label="Crits"
                accent
                onPress={() => navigation.navigate('Subscription')}
              />
            )}
            <StatCard
              value={formatNumber(userData.matchesWon)}
              label="Matches Won"
            />
            <StatCard
              value={formatNumber(userData.tournamentsWon)}
              label="Tournaments Won"
            />
            <StatCard
              value={formatWinRate(userData.matchesWon, userData.matchesLost)}
              label="Win Rate"
            />
          </View>
        </View>

        {/* Earnings: its own section, never in one grid with wins. Results are
            never paid; money comes only from Super Crits and Rumbles. */}
        <View style={styles.section}>
          <SectionHeader icon="wallet" title="Earnings" />
          <TouchableOpacity
            style={[styles.statCard, styles.statCardAccent]}
            onPress={() => navigation.navigate('Wallet')}
            activeOpacity={0.7}
          >
            <Text style={[styles.statValue, styles.statValueAccent]}>{formatBalance(userData.balanceCents)}</Text>
            <Text style={styles.statLabel}>Wallet balance</Text>
            <Ionicons name="arrow-forward" size={16} color="#FCD000" style={styles.statArrow} />
          </TouchableOpacity>
        </View>

        {/* Quick Actions */}
        <View style={styles.section}>
          <SectionHeader 
            icon="flash" 
            title="Quick Actions" 
            subtitle="Your channel, videos and stats"
          />
          
          <MenuCard
            icon="person"
            title="Public Profile"
            subtitle="View and edit your public profile"
            onPress={() => userData?.userName && navigation.navigate('PublicProfile', { userName: userData.userName })}
            variant="primary"
          />

          <MenuCard
            icon="videocam"
            title="Streaming Setup"
            subtitle="Stream key, go live"
            onPress={() => navigation.navigate('StreamingAccounts')}
            variant="featured"
            badge={{ text: 'REQUIRED', type: 'new' }}
          />

          <MenuCard
            icon="film"
            title="My Videos"
            subtitle="Your stream videos — publish, download, manage storage"
            onPress={() => navigation.navigate('MyVideos')}
            variant="primary"
            badge={{ text: 'NEW', type: 'new' }}
          />

          <MenuCard
            icon="stats-chart"
            title="Creator Metrics"
            subtitle={anyEverLaunched
              ? 'Time streamed, viewers, events, Super Crits, Rumbles and memberships'
              : 'Time streamed, viewers, events, Super Crits and Rumbles: your creator numbers'}
            onPress={() => navigation.navigate('CreatorMetrics')}
            variant="primary"
            badge={{ text: 'NEW', type: 'new' }}
          />

          <MenuCard
            icon="ribbon"
            title="Profile Badge"
            subtitle="Choose the badge on your public profile"
            onPress={() => navigation.navigate('ProfileBadge')}
            variant="primary"
          />
          
          <MenuCard
            icon="checkmark"
            title="Verification Center"
            subtitle="ID, email, phone and terms"
            onPress={() => navigation.navigate('Verification')}
            variant="primary"
          />
          
          <MenuCard
            icon="wallet"
            title="Wallet & Earnings"
            subtitle="Track earnings and payouts"
            onPress={() => navigation.navigate('Wallet')}
          />

          <MenuCard
            icon="card"
            title="Plans"
            subtitle="Pick a plan, change it, manage billing"
            onPress={() => navigation.navigate('Subscription')}
            badge={{ text: 'NEW', type: 'new' }}
          />

          {anyEverLaunched && (
            <MenuCard
              icon="star"
              title="Your memberships"
              subtitle={teamEverLaunched ? "Channels and teams you've joined" : "Channels you've joined"}
              onPress={() => navigation.navigate('Memberships')}
            />
          )}

          <MenuCard
            icon="cube"
            title="Order History"
            subtitle="Merch orders, fulfillment status, tracking"
            onPress={() => navigation.navigate('MerchOrderHistory')}
          />

          <MenuCard
            icon="game-controller"
            title="Game Profiles"
            subtitle="Set your in-game names for each game"
            onPress={() => navigation.navigate('GameProfiles')}
          />

          <MenuCard
            icon="link"
            title="Gaming Accounts"
            subtitle="Link PSN, Xbox, Steam, and more"
            onPress={() => navigation.navigate('PlatformAccounts')}
            badge={{ text: 'NEW', type: 'new' }}
          />

          <MenuCard
            icon="document-text"
            title="Tax Info"
            subtitle="W-9 / W-8BEN for IRS 1099 reporting"
            onPress={() => navigation.navigate('TaxInfo')}
          />
        </View>

        {/* Refer a Friend */}
        <ReferralCard />

        {/* Host Program */}
        <View style={styles.section}>
          <SectionHeader
            icon="star"
            title="Host Program"
            subtitle="Create and host your own events"
          />
          
          <MenuCard
            icon="trophy"
            title={userData.isHost ? "Host Dashboard" : "Become a Host"}
            subtitle={userData.isHost ? "Manage the events you host" : "Create and run your own events"}
            onPress={() => navigation.navigate('HostScreen')}
            variant={userData.isHost ? "featured" : "primary"}
          />
        </View>

        {/* Personal Information */}
        <View style={styles.section}>
          <SectionHeader icon="person" title="Personal Information" />
          <View style={styles.infoCard}>
            <InfoRow label="Email" value={userData.email} />
            <InfoRow 
              label="Full Name" 
              value={`${userData.firstName || ''} ${userData.lastName || ''}`.trim()} 
            />
            <InfoRow
              label="Phone"
              value={userData.phone || userData.phoneNumber}
              actionLabel={(userData.phone || userData.phoneNumber) ? 'Update' : 'Add'}
              // Number changes only happen through the verification flow — the
              // new number isn't saved until its SMS code is confirmed.
              onAction={() => navigation.navigate('PhoneVerificationScreen', { userData })}
            />
            <InfoRow label="Date of Birth" value={userData.dob} />
            <InfoRow label="Address" value={addressStr} isLast />
          </View>
        </View>

        {/* Account Settings */}
        <View style={styles.section}>
          <SectionHeader icon="settings" title="Account Settings" />
          
          {!isGoogleUser && (
            <MenuCard
              icon="lock"
              title="Change Password"
              subtitle="Update your account password"
              onPress={() => navigation.navigate('ChangePassword')}
            />
          )}
          
          <MenuCard
            icon="refresh"
            title="Legal & Privacy"
            subtitle="Eligibility, fair play and compensation basics"
            onPress={() => navigation.navigate('Legal')}
          />
          
          <MenuCard
            icon="help"
            title="Help & Support"
            subtitle="FAQs, contact support"
            onPress={() => Linking.openURL('https://docs.critcash.com/support')}
          />
        </View>

        {/* Danger Zone */}
        <View style={styles.section}>
          <View style={styles.dangerZone}>
            <TouchableOpacity 
              style={styles.logoutButton}
              onPress={handleLogout}
            >
              <Text style={styles.logoutButtonText}>Log Out</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.deleteButton}
              onPress={() => navigation.navigate('DeleteAccount')}
            >
              <Text style={styles.deleteButtonText}>Delete Account</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    color: '#666',
    marginTop: 12,
    fontSize: 14,
  },

  // Error State
  errorIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  errorTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  errorText: {
    color: '#666',
    fontSize: 16,
    marginBottom: 24,
  },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 8,
  },
  primaryButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },

  // Profile Header
  profileHeader: {
    alignItems: 'center',
    paddingTop: 20,
    paddingBottom: 30,
    position: 'relative',
  },
  profileHeaderBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 120,
    backgroundColor: '#0a0a0a',
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  avatarContainer: {
    width: 110,
    height: 110,
    borderRadius: 55,
    backgroundColor: '#111',
    borderWidth: 3,
    borderColor: '#FCD000',
    overflow: 'hidden',
    marginBottom: 16,
    position: 'relative',
    ...Platform.select({
      ios: {
        shadowColor: '#FCD000',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 10,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarLoading: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111',
  },
  avatarEditBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FCD000',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#000',
  },
  avatarEditIcon: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  username: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 12,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    gap: 6,
  },
  statusVerified: {
    backgroundColor: '#2ECC7120',
  },
  statusPending: {
    backgroundColor: '#FCD00020',
  },
  statusIcon: {
    fontSize: 12,
    color: '#2ECC71',
  },
  statusIconPending: {
    color: '#FCD000',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2ECC71',
  },
  statusTextPending: {
    color: '#FCD000',
  },
  hostBadge: {
    backgroundColor: '#FCD00030',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  hostBadgeText: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: 'bold',
  },
  memberSince: {
    color: '#555',
    fontSize: 13,
  },

  // Admin Banner
  adminBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FCD00050',
  },
  adminBannerIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  adminBannerText: {
    flex: 1,
    color: '#FCD000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  adminBannerArrow: {
    color: '#FCD000',
    fontSize: 18,
  },

  // Sections
  section: {
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  sectionIcon: {
    fontSize: 22,
    marginRight: 10,
  },
  sectionHeaderText: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  sectionSubtitle: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },

  // Stats Grid
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
  },
  statCardWrapper: {
    width: '50%',
    paddingHorizontal: 6,
    marginBottom: 12,
  },
  statCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#1a1a1a',
    position: 'relative',
  },
  statCardAccent: {
    borderColor: '#FCD00030',
    backgroundColor: '#0f0a00',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 6,
  },
  statValueAccent: {
    color: '#FCD000',
  },
  statLabel: {
    fontSize: 11,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  statArrow: {
    position: 'absolute',
    top: 10,
    right: 12,
    color: '#FCD000',
    fontSize: 14,
  },

  // Menu Cards
  menuCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  menuCardPrimary: {
    borderColor: '#FCD00030',
  },
  menuCardFeatured: {
    backgroundColor: '#0f0f0f',
    borderColor: '#FCD00050',
    borderWidth: 1.5,
  },
  menuCardDanger: {
    backgroundColor: '#1a0a0a',
    borderColor: '#ff4d4d30',
  },
  menuIcon: {
    fontSize: 24,
    marginRight: 14,
  },
  menuContent: {
    flex: 1,
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 2,
  },
  menuTitlePrimary: {
    color: '#FCD000',
  },
  menuTitleDanger: {
    color: '#ff6b6b',
  },
  menuSubtitle: {
    fontSize: 13,
    color: '#666',
  },
  menuBadge: {
    backgroundColor: '#FCD000',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginRight: 10,
  },
  menuBadgeNew: {
    backgroundColor: '#FCD000',
  },
  menuBadgeText: {
    color: '#000',
    fontSize: 10,
    fontWeight: 'bold',
  },
  menuArrow: {
    color: '#444',
    fontSize: 24,
    fontWeight: '300',
  },
  menuArrowDanger: {
    color: '#ff6b6b50',
  },

  // Info Card
  infoCard: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 4,
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  infoRowLast: {
    borderBottomWidth: 0,
  },
  infoLabel: {
    fontSize: 14,
    color: '#666',
  },
  infoValue: {
    fontSize: 14,
    color: '#fff',
    textAlign: 'right',
    flex: 1,
    marginLeft: 16,
  },
  infoValueWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  infoAction: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FCD000',
    textDecorationLine: 'underline',
  },

  // Danger Zone
  dangerZone: {
    backgroundColor: '#0a0a0a',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1a1a1a',
    gap: 12,
  },
  logoutButton: {
    backgroundColor: '#222',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  logoutButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  deleteButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  deleteButtonText: {
    color: '#ff6b6b',
    fontSize: 14,
  },

  // Footer
  footer: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  footerText: {
    color: '#333',
    fontSize: 12,
    marginBottom: 4,
  },
});